import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { seasonalPoojaService, SeasonalPoojaPaymentResponse } from '../../services/seasonalPoojaService';
import { FamilyMember } from '../../services/seasonalPoojaService';
import { trackPurchase, trackEventWithUTM } from '../../utils/analytics';
import SpiritualConfetti from '../SpiritualConfetti';
import './SeasonalPoojaSuccess.css';

interface SuccessLocationState {
  bookingId: string;
  seasonalPoojaName: string;
  eventDate: string;
  eventTime: string;
  primaryMember: FamilyMember;
  additionalMembers: FamilyMember[];
  totalAmount: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  paymentId: string;
  paymentOrderResponse?: SeasonalPoojaPaymentResponse;
}

const SeasonalPoojaSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SuccessLocationState;

  const [showConfetti, setShowConfetti] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
  const [addressSubmitted, setAddressSubmitted] = useState(false);

  useEffect(() => {
    // Redirect if no booking data
    if (!state || !state.bookingId) {
      navigate('/');
      return;
    }

    // GA4 purchase event — fires exactly once for this booking
    trackPurchase({
      transactionId: state.paymentId,
      value: state.totalAmount,
      items: [{
        item_id: 'seasonal_pooja_navratri_2026',
        item_name: state.seasonalPoojaName,
        item_category: 'Seasonal Pooja',
        quantity: 1 + (state.additionalMembers?.length ?? 0),
        price: state.totalAmount,
      }],
      paymentMethod: 'razorpay',
    });
    trackEventWithUTM('spb_booking_confirmed', 'seasonal_pooja_funnel', state.seasonalPoojaName, {
      booking_id: state.bookingId,
      payment_id: state.paymentId,
      total_amount: state.totalAmount,
      member_count: 1 + (state.additionalMembers?.length ?? 0),
    });

    // Pre-populate address form with customer details from payment order or fallback to customer data
    setAddressFormData(prev => ({
      ...prev,
      email: state.paymentOrderResponse?.customerEmail || state.customerEmail,
      phone: state.paymentOrderResponse?.customerPhone || state.customerPhone
    }));

    // Hide confetti after 6 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 6000);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    return () => clearTimeout(timer);
  }, [state, navigate]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getAllMembers = (): string[] => {
    const members: string[] = [];
    
    if (state.primaryMember) {
      members.push(`${state.primaryMember.firstName} ${state.primaryMember.lastName}`);
    }
    
    if (state.additionalMembers && state.additionalMembers.length > 0) {
      state.additionalMembers.forEach(member => {
        members.push(`${member.firstName} ${member.lastName}`);
      });
    }
    
    return members;
  };

  const handleAddressFormChange = (field: string, value: string) => {
    setAddressFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Comprehensive address form validation functions
  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    const trimmed = email.trim();
    if (!trimmed) return { isValid: false, error: 'Email is required' };
    if (trimmed.length > 254) return { isValid: false, error: 'Email must be 254 characters or less' };
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
    
    return { isValid: true };
  };

  const validatePhone = (phone: string): { isValid: boolean; error?: string } => {
    const cleaned = phone.replace(/\s/g, '');
    if (!cleaned) return { isValid: false, error: 'Phone number is required' };
    
    // Check if only digits
    if (!/^\d+$/.test(cleaned)) return { isValid: false, error: 'Phone number should contain only digits' };
    
    // Check if exactly 10 digits
    if (cleaned.length !== 10) return { isValid: false, error: 'Phone number must be exactly 10 digits' };
    
    // Check if starts with valid digit (6-9 for Indian mobile numbers)
    if (!/^[6-9]/.test(cleaned)) return { isValid: false, error: 'Phone number must start with 6, 7, 8, or 9' };
    
    return { isValid: true };
  };

  const validateAddressLine1 = (address: string): { isValid: boolean; error?: string } => {
    const trimmed = address.trim();
    if (!trimmed) return { isValid: false, error: 'Address Line 1 is required' };
    if (trimmed.length > 100) return { isValid: false, error: 'Address Line 1 must be 100 characters or less' };
    return { isValid: true };
  };

  const validateAddressLine2 = (address: string): { isValid: boolean; error?: string } => {
    const trimmed = address.trim();
    if (trimmed && trimmed.length > 80) return { isValid: false, error: 'Address Line 2 must be 80 characters or less' };
    return { isValid: true };
  };

  const validateCity = (city: string): { isValid: boolean; error?: string } => {
    const trimmed = city.trim();
    if (!trimmed) return { isValid: false, error: 'City is required' };
    if (trimmed.length > 50) return { isValid: false, error: 'City must be 50 characters or less' };
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'City should contain only letters and spaces' };
    return { isValid: true };
  };

  const validateState = (state: string): { isValid: boolean; error?: string } => {
    const trimmed = state.trim();
    if (!trimmed) return { isValid: false, error: 'State is required' };
    if (trimmed.length > 50) return { isValid: false, error: 'State must be 50 characters or less' };
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'State should contain only letters and spaces' };
    return { isValid: true };
  };

  const validatePincode = (pincode: string): { isValid: boolean; error?: string } => {
    const trimmed = pincode.trim();
    if (!trimmed) return { isValid: false, error: 'Pincode is required' };
    
    // Check if only digits
    if (!/^\d+$/.test(trimmed)) return { isValid: false, error: 'Pincode should contain only digits' };
    
    // Check if exactly 6 digits
    if (trimmed.length !== 6) return { isValid: false, error: 'Pincode must be exactly 6 digits' };
    
    return { isValid: true };
  };

  const validateAddressForm = (): boolean => {
    const { email, phone, addressLine1, addressLine2, city, state: stateField, pincode } = addressFormData;

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      alert(emailValidation.error);
      return false;
    }

    // Validate phone
    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.isValid) {
      alert(phoneValidation.error);
      return false;
    }

    // Validate address line 1
    const addressLine1Validation = validateAddressLine1(addressLine1);
    if (!addressLine1Validation.isValid) {
      alert(addressLine1Validation.error);
      return false;
    }

    // Validate address line 2 (optional)
    const addressLine2Validation = validateAddressLine2(addressLine2);
    if (!addressLine2Validation.isValid) {
      alert(addressLine2Validation.error);
      return false;
    }

    // Validate city
    const cityValidation = validateCity(city);
    if (!cityValidation.isValid) {
      alert(cityValidation.error);
      return false;
    }

    // Validate state
    const stateValidation = validateState(stateField);
    if (!stateValidation.isValid) {
      alert(stateValidation.error);
      return false;
    }

    // Validate pincode
    const pincodeValidation = validatePincode(pincode);
    if (!pincodeValidation.isValid) {
      alert(pincodeValidation.error);
      return false;
    }

    return true;
  };

  const handleSubmitAddress = async () => {
    if (!validateAddressForm()) return;

    try {
      setIsSubmittingAddress(true);

      await seasonalPoojaService.submitContactDetails(
        state.bookingId,
        {
          email: addressFormData.email.trim(),
          phone: parseInt(addressFormData.phone.trim()),
          addressLine1: addressFormData.addressLine1.trim(),
          addressLine2: addressFormData.addressLine2.trim() || undefined,
          city: addressFormData.city.trim(),
          state: addressFormData.state.trim(),
          pincode: addressFormData.pincode.trim()
        }
      );

      setAddressSubmitted(true);
      setShowAddressForm(false);
      alert('Prasadam delivery address submitted successfully! You will receive updates via email.');
    } catch (error) {
      console.error('Error submitting address:', error);
      alert('Failed to submit address. Please try again or contact support.');
    } finally {
      setIsSubmittingAddress(false);
    }
  };

  const handleSkipAddress = () => {
    setShowAddressForm(false);
    alert('You can add your address later by contacting our support team.');
  };

  if (!state || !state.bookingId) {
    return null;
  }

  return (
    <div className="seasonal-pooja-success">
      {showConfetti && <SpiritualConfetti isActive={showConfetti} />}
      
      <div className="success-container">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">✅</div>
          <h1>Booking Confirmed!</h1>
          <p className="success-message">
            Your {state.seasonalPoojaName} booking has been confirmed successfully. Please add your delivery address for prasadam.
          </p>
          <p className="booking-id">
            Booking ID: <strong>{state.bookingId}</strong>
          </p>
        </div>

        {/* Booking Summary */}
        <div className="booking-summary">
          <h2>📋 Booking Details</h2>
          
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-item">
                <span className="label">Event:</span>
                <span className="value">{state.seasonalPoojaName}</span>
              </div>
              
              <div className="summary-item">
                <span className="label">📅 Date:</span>
                <span className="value">{formatDate(state.eventDate)}</span>
              </div>
              
              {/* <div className="summary-item">
                <span className="label">🕐 Time:</span>
                <span className="value">{state.eventTime}</span>
              </div> */}

              <div className="summary-divider"></div>

              <div className="summary-item">
                <span className="label">👥 Participants:</span>
                <div className="participants-list">
                  {getAllMembers().map((name, index) => (
                    <span key={index} className="participant-name">
                      {index + 1}. {name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-item">
                <span className="label">💰 Amount Paid:</span>
                <span className="value">₹{state.totalAmount.toLocaleString('en-IN')}</span>
              </div>

              <div className="summary-item">
                <span className="label">💳 Payment ID:</span>
                <span className="value payment-id">{state.paymentId}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Address Collection CTA */}
        {!showAddressForm && !addressSubmitted && (
          <div className="address-cta">
            <h2>📍 Prasadam Delivery</h2>
            <p>
              Would you like to add your address to receive prasadam from this special pooja?
            </p>
            <div className="cta-buttons">
              <button
                onClick={() => setShowAddressForm(true)}
                className="btn-primary"
              >
                📦 Add Delivery Address
              </button>
              <button
                onClick={handleSkipAddress}
                className="btn-secondary"
              >
                Skip for Now
              </button>
            </div>
          </div>
        )}

        {/* Address Form */}
        {showAddressForm && (
          <div className="address-form-section">
            <h2>📍 Prasadam Delivery Address</h2>
            <p className="form-description">
              Please provide your complete address for prasadam delivery
            </p>

            <div className="address-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={addressFormData.email}
                    onChange={(e) => handleAddressFormChange('email', e.target.value)}
                    placeholder="your.email@example.com"
                    maxLength={254}
                    title="Please enter a valid email address (max 254 characters)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={addressFormData.phone}
                    onChange={(e) => handleAddressFormChange('phone', e.target.value)}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    pattern="[6-9][0-9]{9}"
                    title="Phone number must be 10 digits starting with 6, 7, 8, or 9"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="addressLine1">
                  Address Line 1 <span className="required">*</span>
                </label>
                <input
                  id="addressLine1"
                  type="text"
                  value={addressFormData.addressLine1}
                  onChange={(e) => handleAddressFormChange('addressLine1', e.target.value)}
                  placeholder="House/Flat number, Street name"
                  maxLength={100}
                  title="Address Line 1 must be 100 characters or less"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">
                    City <span className="required">*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    value={addressFormData.city}
                    onChange={(e) => handleAddressFormChange('city', e.target.value)}
                    placeholder="Enter city"
                    maxLength={50}
                    pattern="[a-zA-Z\s]*"
                    title="City should contain only letters and spaces (max 50 characters)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="state">
                    State <span className="required">*</span>
                  </label>
                  <input
                    id="state"
                    type="text"
                    value={addressFormData.state}
                    onChange={(e) => handleAddressFormChange('state', e.target.value)}
                    placeholder="Enter state"
                    maxLength={50}
                    pattern="[a-zA-Z\s]*"
                    title="State should contain only letters and spaces (max 50 characters)"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pincode">
                    Pincode <span className="required">*</span>
                  </label>
                  <input
                    id="pincode"
                    type="text"
                    value={addressFormData.pincode}
                    onChange={(e) => handleAddressFormChange('pincode', e.target.value)}
                    placeholder="Enter 6-digit pincode"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    title="Pincode must be exactly 6 digits"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressLine2"> Landmark (Optional)</label>
                  <input
                    id="addressLine2"
                    type="text"
                    value={addressFormData.addressLine2}
                    onChange={(e) => handleAddressFormChange('addressLine2', e.target.value)}
                    placeholder="Apartment, Suite, etc."
                    maxLength={80}
                    title="Landmark must be 80 characters or less"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddressForm(false)}
                  className="btn-secondary"
                  disabled={isSubmittingAddress}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAddress}
                  className="btn-primary"
                  disabled={isSubmittingAddress}
                >
                  {isSubmittingAddress ? 'Submitting...' : 'Submit Address'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Address Submitted Confirmation */}
        {addressSubmitted && (
          <div className="address-submitted">
            <div className="success-icon small">✅</div>
            <h3>Address Submitted Successfully!</h3>
            <p>
              We have received your prasadam delivery address. You will receive updates about the delivery via email and Whatsapp.
            </p>
          </div>
        )}

        {/* Next Steps */}
        <div className="next-steps">
          <h2>📝 What's Next?</h2>
          <div className="steps-list">
            <div className="step-item">
              <span className="step-number">1</span>
              <div className="step-content">
                <h4>Confirmation Email</h4>
                <p>You will receive a confirmation email with all booking details.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <div className="step-content">
                <h4>Event Participation</h4>
                <p>Sankalp in your name will be executed during {state.seasonalPoojaName} on {formatDate(state.eventDate)}.</p>
              </div>
            </div>
            {addressSubmitted && (
              <div className="step-item">
                <span className="step-number">3</span>
                <div className="step-content">
                  <h4>Prasadam Delivery</h4>
                  <p>You will receive blessed prasadam at your registered address within 7-10 days.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="final-actions">
          <button
            onClick={() => navigate('/')}
            className="btn-home"
          >
             Back to Home
          </button>
          <button
            onClick={() => window.print()}
            className="btn-print"
          >
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeasonalPoojaSuccess;